'use client';

import { Modals } from '@/containers/Modals/all-modals';
import ReportIconSvg from '@/assets/svgs/report.svg';
import CopyIconSvg from '@/assets/svgs/copy.svg';
import ModalBody from '@/containers/Modals/ModalBody';
import ModalContainer from '@/containers/Modals/ModalContainer';
import ModalHeader from '@/containers/Modals/ModalHeader';
import useModals from '@/containers/Modals/useModals';
import { ModalProps } from '@/containers/Modals/types';
import { generateReport } from '@/containers/Reports/api';
import { AVAILABLE_REPORTS, getReportComponent } from '@/containers/Reports/registry';
import { ReportContext, ReportFormValues, ReportType } from '@/containers/Reports/types';
import { copyToClipboard } from '@/shared/utils/clipboard';
import useToast from '@/containers/ToastMessage/hooks/useToast';
import { useEffect, useMemo, useState } from 'react';

// Defines the params expected in the URL query string
export interface ExportReportModalParams {
  reportCtx?: ReportContext;
  jobId?: string;
  accountId?: string;
}

const ExportReportModal = ({
  variant = 'narrow',
  params,
}: {
  variant?: ModalProps['variant'];
  params?: ExportReportModalParams;
}) => {
  // Use URL-based state management
  const { isModalOpen, onClose } = useModals({
    modalName: Modals.ExportReportModal,
    shouldCloseOnEscape: true,
  });

  const { showSuccess } = useToast();

  const context = params?.reportCtx || ('job-list' as ReportContext);
  const jobId = params?.jobId;
  const accountId = params?.accountId;

  // State
  const [selectedReportType, setSelectedReportType] = useState<ReportType | null>(null);
  const [formValues, setFormValues] = useState<ReportFormValues>({
    startDate: new Date().toISOString().split('T')[0], // Default to today
    endDate: new Date().toISOString().split('T')[0],
  });
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Available reports for the current context
  const availableReports = useMemo(() => AVAILABLE_REPORTS[context] || [], [context]);

  // Auto-select report type if only one is available or not yet selected
  useEffect(() => {
    if (isModalOpen && availableReports.length > 0 && !selectedReportType) {
      setSelectedReportType(availableReports[0]);
    }
  }, [isModalOpen, availableReports, selectedReportType]);

  // Reset state when modal closes
  useEffect(() => {
    if (!isModalOpen) {
      setGeneratedLink(null);
      setError(null);
      setIsGenerating(false);
    }
  }, [isModalOpen]);

  // Form handler
  const handleFormChange = (
    field: keyof ReportFormValues,
    value: ReportFormValues[keyof ReportFormValues]
  ) => {
    setFormValues((prev) => ({ ...prev, [field]: value }));
  };

  // Generate handler
  const handleGenerate = async () => {
    if (!selectedReportType) return;
    setIsGenerating(true);
    setError(null);
    setGeneratedLink(null);

    try {
      const response = await generateReport({
        type: selectedReportType,
        accountId,
        jobId,
        payload: {
          start_date: formValues.startDate,
          end_date: formValues.endDate,
          create_suppression_list: formValues.createSuppressionList || false,
          create_phone_list: formValues.createPhoneList || false,
          suppression_list_name: formValues.suppressionListName,
          phone_list_name: formValues.phoneListName,
        },
      });

      if (response && response.link) {
        setGeneratedLink(response.link);
        // Auto-download
        window.open(response.link, '_blank');
      } else {
        setError('Failed to generate report link.');
      }
    } catch (err: unknown) {
      console.error('Report generation error:', err);
      const message =
        err instanceof Error
          ? err.message
          : 'An error occurred while generating the report.';
      setError(message);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = () => {
    if (generatedLink) {
      copyToClipboard(generatedLink, showSuccess, 'Link copied!');
    }
  };

  if (!isModalOpen) return null;

  const ReportComponent = selectedReportType
    ? getReportComponent(selectedReportType)
    : null;

  return (
    <div className="relative">
      <input
        type="checkbox"
        id={Modals.ExportReportModal}
        className="modal-toggle"
        checked={isModalOpen}
        onChange={() => {}}
      />
      <ModalContainer
        isModalOpen={isModalOpen}
        modalName={Modals.ExportReportModal}
        variant={variant}
        onClose={onClose}>
        <div className="flex h-full flex-col">
          <ModalHeader
            modalName={Modals.ExportReportModal}
            closeAction="url"
            title="Export Report"
            icon={<ReportIconSvg className="h-5 w-5" />}
          />

          <ModalBody className="flex-1 px-6">
            {/* Dynamic Report Form */}
            {ReportComponent && (
              <ReportComponent
                values={formValues}
                onChange={handleFormChange}
                isGenerating={isGenerating}
                accountId={accountId}
                jobId={jobId}
                availableReports={availableReports}
                selectedReportType={selectedReportType}
                onReportTypeChange={(type: ReportType) => {
                  setSelectedReportType(type);
                  setGeneratedLink(null);
                  setError(null);
                }}
              />
            )}

            {/* Generate Button */}
            <div className="mt-8">
              <button
                className="btn btn-primary"
                onClick={handleGenerate}
                disabled={
                  isGenerating ||
                  !selectedReportType ||
                  !formValues.startDate ||
                  !formValues.endDate
                }>
                {isGenerating ? (
                  <span className="loading loading-spinner"></span>
                ) : (
                  'Generate Report'
                )}
              </button>
            </div>

            {/* Result Section */}
            {error && <div className="text-error mt-4">{error}</div>}

            {generatedLink && (
              <div className="mt-6 flex flex-col gap-4">
                <div className="form-control">
                  <label className="label">
                    <span className="label-text font-medium text-gray-700 dark:text-gray-300">
                      Report Link:
                    </span>
                  </label>
                  <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 dark:border-gray-700 dark:bg-gray-800">
                    <span
                      className="flex-1 truncate text-sm text-gray-600 dark:text-gray-300"
                      title={generatedLink}>
                      {generatedLink}
                    </span>
                    <button
                      className="btn btn-ghost btn-xs ml-4"
                      onClick={handleCopy}
                      type="button"
                      aria-label="Copy link">
                      <CopyIconSvg className="h-4 w-4" />
                      <span className="sr-only">Copy</span>
                    </button>
                  </div>
                </div>

                <div>
                  <a
                    href={generatedLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn btn-success text-white">
                    Download Now
                  </a>
                </div>
              </div>
            )}

            {/* Bottom spacer */}
            <div className="h-8"></div>
          </ModalBody>
        </div>
      </ModalContainer>
    </div>
  );
};

export default ExportReportModal;
